import { useCallback, useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { RotateCcw, AlertCircle, Coins, ArrowLeft, Layers } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, errMsg, hasPlan } from "@/lib/api";
import { fileIssue } from "@/lib/limits";
import { TOOLS } from "@/lib/tools";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/i18n";
import { Dropzone } from "@/components/Dropzone";
import { OptionGroup } from "@/components/OptionGroup";
import { DownloadPanel } from "@/components/DownloadPanel";
import { ResultView } from "@/components/ResultView";
import { BatchList } from "@/components/BatchList";
import { CreditAlert, GoogleOnlyAlert } from "@/components/CreditAlert";
import { ToolFaq } from "@/components/ToolFaq";
import { RelatedLinks } from "@/components/RelatedLinks";
import { Breadcrumb } from "@/components/Breadcrumb";
import PAGES from "@/seo/pages.json";

const Processing = ({ tool, file }) => {
  const { t } = useLang();
  return (
    <div data-testid="processing-state" className="scanline relative flex min-h-[420px] flex-col items-center justify-center overflow-hidden rounded-2xl border border-slate-200 bg-white p-10 text-center">
      <div className="flex gap-2">
        {["#00A3E0", "#E40046", "#FFB800", "#0F172A"].map((c, i) => (
          <span key={c} className="h-3 w-3 animate-bounce rounded-full" style={{ background: c, animationDelay: `${i * 0.12}s` }} />
        ))}
      </div>
      <p className="mt-6 font-display text-xl font-semibold text-slate-900">{t("tool.processingTitle", { file })}</p>
      <p className="mt-2 max-w-sm text-sm text-slate-500">{tool.key === "upscale" ? t("tool.processingSlow") : t("tool.processingFast")}</p>
    </div>
  );
};

const UpscaleOptions = ({ opts, setOpts }) => {
  const { t } = useLang();
  return (
    <div className="space-y-5">
      <OptionGroup label={t("tool.quality")} cols={2} value={opts.mode} onChange={(mode) => setOpts((o) => ({ ...o, mode }))} testPrefix="upscale-mode"
        options={[{ value: "ultra", label: t("tool.ultra"), hint: t("tool.ultraHint") }, { value: "fast", label: t("tool.fast"), hint: t("tool.fastHint") }]} />
      <OptionGroup label={t("tool.scale")} cols={2} value={opts.scale} onChange={(scale) => setOpts((o) => ({ ...o, scale }))} testPrefix="upscale-scale"
        options={[{ value: 2, label: "2x", hint: t("tool.x2Hint") }, { value: 4, label: "4x", hint: t("tool.x4Hint") }]} />
    </div>
  );
};

const MAX_BATCH = 50;
const toolOptions = (key, o) => (key === "upscale" ? { mode: o.mode, scale: o.scale } : key === "vectorize" ? { enhance: o.enhance } : {});

const VectorOptions = ({ opts, setOpts }) => {
  const { t } = useLang();
  return (
    <OptionGroup label={t("vec.enhance")} cols={2} value={opts.enhance} onChange={(enhance) => setOpts((o) => ({ ...o, enhance }))} testPrefix="vector-enhance"
      options={[{ value: true, label: t("vec.enhanceOn"), hint: t("vec.enhanceOnHint") }, { value: false, label: t("vec.enhanceOff"), hint: t("vec.enhanceOffHint") }]} />
  );
};

const SidePanel = ({ job, tool, page, opts, setOpts, credits, onDownloaded, engine }) => {
  const { t, href } = useLang();
  if (job?.status === "done")
    return (
      <>
        <p className="font-mono text-[11px] uppercase tracking-wider text-slate-400">{t("tool.export")}</p>
        {job.width && <p className="mt-1 font-display text-lg font-semibold text-slate-900" data-testid="result-dimensions">{job.width} × {job.height} px</p>}
        {job.pages && <p className="mt-1 font-display text-lg font-semibold text-slate-900">{t(job.pages === 1 ? "tool.page1" : "tool.pages", { n: job.pages })}</p>}
        <div className="mt-5"><DownloadPanel key={job.job_id} job={job} tool={tool} page={page} engine={engine} onDownloaded={onDownloaded} /></div>
      </>
    );
  return (
    <>
      <p className="font-mono text-[11px] uppercase tracking-wider text-slate-400">{t("tool.settings")}</p>
      <div className="mt-4">
        {tool.key === "vectorize" && <div className="mb-5"><VectorOptions opts={opts} setOpts={setOpts} /></div>}
        {tool.key === "upscale" ? <UpscaleOptions opts={opts} setOpts={setOpts} /> : (
          <ul className="space-y-2 text-sm text-slate-600">{t(`tools.${tool.slug}.badges`).map((b) => <li key={b} className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full" style={{ background: tool.color }} />{b}</li>)}</ul>
        )}
      </div>
      <p className="mt-6 text-xs text-slate-500">{t(`tool.chooseLater.${tool.key}`)}</p>
      <div className="mt-6 flex items-center justify-between rounded-lg bg-slate-50 p-3">
        <span className="flex items-center gap-2 text-sm text-slate-600"><Coins className="h-4 w-4" />{t("tool.costs")}</span>
        {credits !== null && <span className="whitespace-nowrap font-mono text-xs font-semibold text-slate-900" data-testid="tool-credit-balance">{t("tool.balance", { n: credits })}</span>}
      </div>
      {credits !== null && credits < 1 && <Button asChild className="mt-3 w-full rounded-full" data-testid="tool-buy-credits"><Link to={href("pricing")}>{t("tool.buy")}</Link></Button>}
    </>
  );
};

export default function ToolPage({ slug, pageKey }) {
  const tool = TOOLS[slug];
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, refresh } = useAuth();
  const { t, tErr, lang, href } = useLang();
  const location = useLocation();
  const [job, setJob] = useState(null);
  const [opts, setOpts] = useState({ mode: "ultra", scale: 4, enhance: true });
  const [engine, setEngine] = useState("recraft");
  const [uploading, setUploading] = useState(false);
  const [page, setPage] = useState(0);
  const [batch, setBatch] = useState(null);
  const [batchMsg, setBatchMsg] = useState("");
  const [limitMsg, setLimitMsg] = useState("");
  const jobId = params.get("job");
  const plan = hasPlan(user);

  const poll = useCallback(async (id) => {
    try { const { data } = await api.get(`/jobs/${id}`); setJob(data); return data; }
    catch (e) { toast.error(tErr(errMsg(e))); setParams({}); return null; }
  }, [setParams, tErr]);

  useEffect(() => { if (!tool) navigate("/painel"); }, [tool, navigate]);
  useEffect(() => { setPage(0); setEngine("recraft"); if (jobId && user) poll(jobId); else setJob(null); }, [jobId, !!user]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!job?.engines?.length) return;
    const saved = localStorage.getItem(`engine:${job.job_id}`);
    setEngine(job.engines.includes(saved) ? saved : job.engines.includes(engine) ? engine : job.engines[0]);
  }, [job?.job_id, job?.engines?.length]); // eslint-disable-line react-hooks/exhaustive-deps
  const chooseEngine = (e) => { setEngine(e); if (job) localStorage.setItem(`engine:${job.job_id}`, e); };
  useEffect(() => {
    if (job?.status !== "processing") return;
    const timer = setInterval(async () => {
      const d = await poll(job.job_id);
      if (d?.status === "done") toast.success(t("tool.ready"));
      if (d?.status === "failed") { toast.error(tErr(d.error)); refresh(); }
    }, 2500);
    return () => clearInterval(timer);
  }, [job?.status, job?.job_id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!batch?.some((i) => i.status === "processing")) return;
    const timer = setInterval(async () => {
      const ids = batch.filter((i) => i.status === "processing").map((i) => i.job_id);
      const fresh = await Promise.all(ids.map((id) => api.get(`/jobs/${id}`).then((r) => r.data).catch(() => null)));
      const map = Object.fromEntries(fresh.filter(Boolean).map((d) => [d.job_id, d]));
      setBatch((b) => b.map((i) => (map[i.job_id] ? { ...i, ...map[i.job_id] } : i)));
    }, 3000);
    return () => clearInterval(timer);
  }, [batch]);

  if (!tool) return null;
  const Icon = tool.icon;
  const seo = PAGES[pageKey][lang];

  const upload = async (file) => {
    if (!user) { navigate(`/entrar?next=${encodeURIComponent(location.pathname)}`); return; }
    if (!user.can_use_credits) { toast.error(t("alert.googleOnly")); return; }
    const issue = await fileIssue(file, t);
    if (issue) { setLimitMsg(issue); toast.error(issue); return; }
    const fd = new FormData();
    fd.append("tool", tool.key);
    fd.append("file", file);
    fd.append("options", JSON.stringify(toolOptions(tool.key, opts)));
    setUploading(true);
    try {
      const { data } = await api.post("/jobs", fd);
      setJob(data);
      setParams({ job: data.job_id });
      refresh();
    } catch (e) { toast.error(tErr(errMsg(e))); }
    finally { setUploading(false); }
  };

  const uploadBatch = async (files) => {
    const list = files.slice(0, MAX_BATCH);
    if (files.length > MAX_BATCH) toast(t("batch.max", { n: MAX_BATCH }));
    setBatch(list.map((f, i) => ({ key: `${Date.now()}-${i}`, input_name: f.name, status: "queued" })));
    setUploading(true);
    for (let i = 0; i < list.length; i++) {
      setBatchMsg(t("batch.uploading", { i: i + 1, n: list.length }));
      let patch;
      const issue = await fileIssue(list[i], t);
      if (issue) { patch = { status: "failed", error: issue }; setBatch((b) => b.map((it, j) => (j === i ? { ...it, ...patch } : it))); continue; }
      const fd = new FormData();
      fd.append("tool", tool.key);
      fd.append("file", list[i]);
      fd.append("batch", "true");
      fd.append("options", JSON.stringify(toolOptions(tool.key, opts)));
      try { const { data } = await api.post("/jobs", fd); patch = data; }
      catch (e) { patch = { status: "failed", error: errMsg(e) }; }
      setBatch((b) => b.map((it, j) => (j === i ? { ...it, ...patch } : it)));
    }
    setBatchMsg("");
    setUploading(false);
  };

  const onFiles = (files) => {
    setLimitMsg("");
    if (!user) { navigate(`/entrar?next=${encodeURIComponent(location.pathname)}`); return; }
    if (files.length === 1) return upload(files[0]);
    if (!plan) { toast(t("batch.planOnly")); return upload(files[0]); }
    if (!user.can_use_credits) { toast.error(t("alert.googleOnly")); return; }
    uploadBatch(files);
  };

  const onDownloaded = async () => {
    refresh();
    const d = await poll(job.job_id);
    if (d) setBatch((b) => b && b.map((i) => (i.job_id === d.job_id ? { ...i, ...d } : i)));
  };

  const reset = () => { setParams({}); setJob(null); setBatch(null); };
  const isPdf = tool.key === "pdf";

  return (
    <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8" data-testid={`tool-page-${slug}`}>
      <Breadcrumb pageKey={pageKey} />
      <div className="flex flex-wrap items-start justify-between gap-6">
        <div className="flex items-start gap-4">
          <span className="grid h-12 w-12 shrink-0 place-items-center rounded-xl text-white" style={{ background: tool.color }}><Icon className="h-6 w-6" /></span>
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl" data-testid="tool-title">{seo.h1}</h1>
            <p className="mt-2 max-w-2xl text-sm text-slate-600 md:text-base">{seo.intro}</p>
          </div>
        </div>
        <div className="flex gap-2">
          {job && batch && <Button variant="outline" onClick={() => setParams({})} className="rounded-full" data-testid="back-to-batch-button"><ArrowLeft className="mr-2 h-4 w-4" />{t("batch.back")}</Button>}
          {(job || batch) && <Button variant="outline" onClick={reset} disabled={uploading} className="rounded-full" data-testid="new-file-button"><RotateCcw className="mr-2 h-4 w-4" />{t("tool.newFile")}</Button>}
        </div>
      </div>

      <div className="mt-8 space-y-3">
        {user && <GoogleOnlyAlert user={user} />}
        {user && <CreditAlert user={user} />}
      </div>

      <div className="mt-10 grid gap-8 lg:grid-cols-12">
        <div className="lg:col-span-8">
          {!job && batch && (
            <>
              {batchMsg && <p className="mb-3 font-mono text-xs text-slate-500" data-testid="batch-upload-status">{batchMsg}</p>}
              <BatchList items={batch} tool={tool} onOpen={(id) => setParams({ job: id })} />
            </>
          )}
          {!job && !batch && (
            <>
              <Dropzone accept={tool.accept} onFiles={onFiles} multiple={plan} disabled={uploading} cta={t("tool.clickOrDrag")}
                label={uploading ? t("tool.uploading") : isPdf ? t("tool.uploadPdf") : t("tool.uploadImg")}
                hint={`${isPdf ? t("tool.hintPdf") : t("tool.hintImg")}${plan ? ` · ${t("batch.hint")}` : ""}`} />
              {limitMsg && (
                <div data-testid="upload-limit-alert" className="mt-3 flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 p-4">
                  <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-600" />
                  <p className="text-sm text-red-800">{limitMsg}</p>
                </div>
              )}
              {user && !plan && (
                <Link to={href("pricing")} className="mt-3 inline-flex items-center gap-2 text-xs text-slate-500 hover:text-slate-900" data-testid="batch-upsell-link">
                  <Layers className="h-3.5 w-3.5" />{t("batch.upsell")}
                </Link>
              )}
            </>
          )}
          {job?.status === "processing" && <Processing tool={tool} file={job.input_name} />}
          {job?.status === "failed" && (
            <div data-testid="job-failed" className="flex min-h-[320px] flex-col items-center justify-center rounded-2xl border border-red-200 bg-red-50 p-10 text-center">
              <AlertCircle className="h-8 w-8 text-red-500" />
              <p className="mt-4 font-semibold text-red-700">{tErr(job.error)}</p>
              <Button onClick={reset} className="mt-6 rounded-full" data-testid="retry-button">{t("tool.tryAnother")}</Button>
            </div>
          )}
          {job?.status === "done" && <ResultView job={job} tool={tool} page={page} setPage={setPage} engine={engine} setEngine={chooseEngine} />}
        </div>
        <aside className="lg:col-span-4">
          <div className="sticky top-44 rounded-2xl border border-slate-200 bg-white p-6">
            <SidePanel job={job} tool={tool} page={page} opts={opts} setOpts={setOpts} credits={user ? (user.unlimited ? "∞" : user.credits) : null} onDownloaded={onDownloaded} engine={engine} />
          </div>
        </aside>
      </div>
      {seo.sections && (
        <section className="mt-20 grid max-w-5xl gap-8 md:grid-cols-2" data-testid="tool-sections">
          {seo.sections.map((s) => (
            <div key={s.h}>
              <h2 className="text-xl font-semibold text-slate-900">{s.h}</h2>
              <p className="mt-3 text-sm leading-relaxed text-slate-600 md:text-base">{s.p}</p>
            </div>
          ))}
        </section>
      )}
      <ToolFaq faq={seo.faq} />
      <RelatedLinks tool={slug} current={pageKey} />
    </div>
  );
}
